/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

import type { NewAccountData } from '../../components/Accounts/types';

export type PartnerAccountBusinessCaseRequest = NewAccountData & {
  newAccountId: string;
};

export type PartnerAccountBusinessCaseResponse = {
  success: boolean;
  errorMessage: string;
};

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  PartnerAccountBusinessCaseResponse | undefined,
  (requestParams: PartnerAccountBusinessCaseRequest) => void,
  () => void,
];

function usePostPartnerAccountBusinessCase({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<PartnerAccountBusinessCaseRequest>();
  const [response, setResponse] = useState<PartnerAccountBusinessCaseResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: PartnerAccountBusinessCaseRequest) => {
      setDataFetchingInfo(true, 'sending business case data', 'fullScreen');

      const { Success, ErrorMessage } = await api.postPartnerAccountBusinessCase(buildParams(requestParams), source);

      const res = {
        success: Success,
        errorMessage: ErrorMessage || '',
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams?: PartnerAccountBusinessCaseRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function getWebsiteURL(websiteURL: string, companyWebsiteURL: string) {
    if (websiteURL !== '') {
      return vp.sanatizeURL(websiteURL);
    }

    if (companyWebsiteURL !== '') {
      return vp.sanatizeURL(companyWebsiteURL);
    }

    return undefined;
  }

  function buildParams(requestParams: PartnerAccountBusinessCaseRequest): vp.PartnerAccountBusinessCaseRequest {
    const {
      businessName,
      firstName,
      lastName,
      emailAddress,
      businessEmailAddress,
      notes,
      websiteURL,
      companyWebsiteURL,
      businessModel,
      numberOfYears,
      industry,
      isMSB,
      isISO,
      newAccountId,
      platformUsage,
      companyPlatformUsage,
      businessPhone,
      createBillingPackage,
      packageType,
      reserveToActivate,
      dataAggregator,
      systemOfRecord,
      socialMediaPage,
      companyPhone,
      legalAgreementNotes,
    } = requestParams;
    // add information for user
    return vp.removeEmptyProperty<vp.PartnerAccountBusinessCaseRequest>({
      VopayAccountID: newAccountId,
      BusinessName: businessName || `${firstName ?? ''} ${lastName ?? ''}`,
      FirstName: firstName,
      LastName: lastName,
      BusinessEmail: emailAddress ?? businessEmailAddress ?? '',
      BusinessPhone: businessPhone,
      BillingPackagesID: packageType,
      WebsiteURL: getWebsiteURL(websiteURL ?? '', companyWebsiteURL ?? ''),
      BusinessModel: businessModel,
      NumberofYearsInBusiness: numberOfYears,
      Industry: industry,
      MSB: isMSB,
      ISO: isISO,
      Notes: notes,
      PlatformUsage: platformUsage !== '' ? platformUsage : companyPlatformUsage ?? '',
      DataAggregator: dataAggregator,
      SystemOfRecord: systemOfRecord,
      ReserveToActivate: vp.sanitizeToNumeric(reserveToActivate),
      createBillingPackage: createBillingPackage || false,
      SocialMediaPage: socialMediaPage || '',
      CompanyPhone: companyPhone || '',
      LegalAgreementNotes: legalAgreementNotes || '',
    });
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostPartnerAccountBusinessCase);
